import { useQuery, UseQueryResult } from '@tanstack/react-query';

export const MAINTENANCE_CONFIG_QUERY_KEY = ['MaintenanceConfig'];
const SUCCESS_REFETCH_INTERVAL_IN_MS = 20 * 1000;
const ERROR_REFETCH_INTERVAL_IN_MS = 5 * 60 * 1000;
const NO_MAINTENANCE_URL_WARNING = 'Er is geen maintenance url gedefinieerd.';
const INVALID_MAINTENANCE_URL_ERROR = 'De maintenance url is ongeldig.';

let hasWarnedAboutMissingMaintenanceUrl = false;
let hasLoggedInvalidMaintenanceUrl = false;

interface MaintenanceConfig {
  maintenanceMode: boolean;
  message?: string;
}

const getMaintenanceConfigUrl = (): string => window.MAINTENANCE_CONFIG_URL?.trim() ?? '';

const isValidMaintenanceConfigUrl = (maintenanceConfigUrl: string): boolean => {
  try {
    new URL(maintenanceConfigUrl);
    return true;
  } catch {
    return false;
  }
};

const warnAboutMissingMaintenanceUrl = (): void => {
  if (hasWarnedAboutMissingMaintenanceUrl) return;

  console.warn(NO_MAINTENANCE_URL_WARNING);
  hasWarnedAboutMissingMaintenanceUrl = true;
};

const logInvalidMaintenanceUrl = (): void => {
  if (hasLoggedInvalidMaintenanceUrl) return;

  console.error(INVALID_MAINTENANCE_URL_ERROR);
  hasLoggedInvalidMaintenanceUrl = true;
};

const canFetchMaintenanceConfig = (maintenanceConfigUrl: string): boolean => {
  if (!maintenanceConfigUrl) {
    warnAboutMissingMaintenanceUrl();
    return false;
  }

  if (!isValidMaintenanceConfigUrl(maintenanceConfigUrl)) {
    logInvalidMaintenanceUrl();
    return false;
  }

  return true;
};

const getMaintenanceConfig = async (): Promise<MaintenanceConfig> => {
  const maintenanceConfigUrl = getMaintenanceConfigUrl();

  if (!canFetchMaintenanceConfig(maintenanceConfigUrl)) {
    return { maintenanceMode: false };
  }

  const response = await fetch(maintenanceConfigUrl, { cache: 'no-store' });

  if (!response.ok) {
    throw new Error('Onderhoudsconfiguratie kon niet worden opgehaald.');
  }

  return response.json();
};

export const useGetMaintenanceConfig = (): UseQueryResult<MaintenanceConfig> => {
  const maintenanceConfigUrl = getMaintenanceConfigUrl();
  const isMaintenanceConfigEnabled = canFetchMaintenanceConfig(maintenanceConfigUrl);

  return useQuery({
    queryKey: MAINTENANCE_CONFIG_QUERY_KEY,
    queryFn: getMaintenanceConfig,
    enabled: isMaintenanceConfigEnabled,
    gcTime: 0,
    refetchInterval: query => (query.state.status === 'error' ? ERROR_REFETCH_INTERVAL_IN_MS : SUCCESS_REFETCH_INTERVAL_IN_MS),
  });
};
